/**
 * Types for conjugation (*tiṅanta*).
 *
 * Sanskrit's finite verb is described by five things: the root, the *lakāra*
 * (the tense-mood slot), the *prayoga* (voice), the *puruṣa* (person) and the
 * *vacana* (number). The root additionally belongs to a *gaṇa* (one of ten
 * classes, which decides its present stem) and takes one or both *padas*.
 */

/** The ten classes of verbal root, which decide how the present stem is formed. */
export type Gana =
    | "Bhvadi" | "Adadi" | "Juhotyadi" | "Divadi" | "Svadi" | "Tudadi"
    | "Rudhadi" | "Tanadi" | "Kryadi" | "Curadi" | "Kandvadi";

/**
 * The ten lakāras. Pāṇini names the tense-mood slots with these abstract labels
 * rather than by meaning; the glosses are how they are usually taught.
 */
export type Lakara =
    | "Lat"        // present
    | "Lit"        // perfect
    | "Lut"        // periphrastic future
    | "Lrt"        // simple future
    | "Let"        // subjunctive (Vedic)
    | "Lot"        // imperative
    | "Lan"        // imperfect
    | "VidhiLin"   // optative
    | "AshirLin"   // benedictive
    | "Lun"        // aorist
    | "Lrn";       // conditional

export type Purusha = "Prathama" | "Madhyama" | "Uttama";
export type Vacana = "Eka" | "Dvi" | "Bahu";
export type Prayoga = "Kartari" | "Karmani" | "Bhave";
export type Pada = "Parasmaipada" | "Atmanepada";

/** The lakāras worth showing, in teaching order, with their usual English gloss. */
export const LAKARAS: Array<{ id: Lakara; name: string; gloss: string }> = [
    { id: "Lat", name: "laṭ", gloss: "present" },
    { id: "Lan", name: "laṅ", gloss: "imperfect" },
    { id: "Lot", name: "loṭ", gloss: "imperative" },
    { id: "VidhiLin", name: "vidhiliṅ", gloss: "optative" },
    { id: "Lit", name: "liṭ", gloss: "perfect" },
    { id: "Lut", name: "luṭ", gloss: "periphrastic future" },
    { id: "Lrt", name: "lṛṭ", gloss: "future" },
    { id: "Lun", name: "luṅ", gloss: "aorist" },
    { id: "AshirLin", name: "āśīrliṅ", gloss: "benedictive" },
    { id: "Lrn", name: "lṛṅ", gloss: "conditional" },
];

/**
 * Person, in Pāṇini's order. Note this is the reverse of the European
 * convention: *prathama* ("first") is the third person.
 */
export const PURUSHAS: Array<{ id: Purusha; name: string; gloss: string }> = [
    { id: "Prathama", name: "prathama", gloss: "3rd" },
    { id: "Madhyama", name: "madhyama", gloss: "2nd" },
    { id: "Uttama", name: "uttama", gloss: "1st" },
];

export const VACANAS: Array<{ id: Vacana; name: string; gloss: string }> = [
    { id: "Eka", name: "eka", gloss: "singular" },
    { id: "Dvi", name: "dvi", gloss: "dual" },
    { id: "Bahu", name: "bahu", gloss: "plural" },
];

/** A verbal root as the engine needs it stated. */
export type Dhatu = {
    /**
     * The *aupadeśika* form — the root as Pāṇini states it, with the accent
     * marks and `it` letters that condition later rules. `ga\mx~`, not `gam`.
     * These are load-bearing: the accent decides which pada the root takes, and
     * dropping it changes the derivation.
     */
    aupadeshika: string;
    gana: Gana;
    /** Preverbs, e.g. `["sam", "A"]` for *samā-gacchati*. */
    prefixes?: string[];
    /**
     * Affixes that build a new verb from the root before any tense ending:
     * causative, desiderative, intensive.
     */
    sanadi?: string[];
};

/**
 * The *sanādi* affixes, which build a new verb from a root before any tense
 * ending is added: a causative "makes X do", a desiderative "wants to do", an
 * intensive "does repeatedly or intensely".
 *
 * They are offered for conjugation but not indexed for reverse lookup — each one
 * multiplies the form space, and the index is already 1.9 MB.
 */
export type Sanadi = "Ric" | "san" | "yaN" | "yaNluk";

export const SANADIS: Array<{ id: Sanadi; name: string; gloss: string }> = [
    { id: "Ric", name: "ṇic", gloss: "causative — “makes (someone) do”" },
    { id: "san", name: "san", gloss: "desiderative — “wants to do”" },
    { id: "yaN", name: "yaṅ", gloss: "intensive — “does repeatedly”" },
    { id: "yaNluk", name: "yaṅluk", gloss: "intensive, without the affix" },
];
